import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, ArrowRight, User, Search, Trophy, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FeatureToggle from '../components/FeatureToggle';
import SEO from '../components/SEO';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1780362430939-d4a5084a38ae?w=1920&q=80';
// BJJ private training, mat work, grappling (existing trusted Unsplash asset)

const DISCIPLINE_TRACKS = [
  { id: 'jiujitsu', label: 'Brazilian Jiu-Jitsu' },
  { id: 'self_defense', label: 'Self-Defense' },
  { id: 'personal_training', label: 'Personal Training' },
];

const BELTS = [
  { name: 'White', cls: 'belt-white' },
  { name: 'Blue', cls: 'belt-blue' },
  { name: 'Purple', cls: 'belt-purple' },
  { name: 'Brown', cls: 'belt-brown' },
  { name: 'Black', cls: 'belt-black' },
];

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isClient } = useAuth();
  const [activeTrack, setActiveTrack] = React.useState('jiujitsu');

  const categories = [
    {
      id: 'personal_training',
      slug: 'personal-training',
      title: 'Personal Training',
      description: 'Health, strength, and resilience. Build your foundation.',
      image: 'https://images.unsplash.com/photo-1648542036561-e1d66a5ae2b1?w=800',
    },
    {
      id: 'self_defense',
      slug: 'self-defense',
      title: 'Self Defense',
      description: 'Awareness, escape, and real-world readiness.',
      image: 'https://images.unsplash.com/photo-1611077479643-5b3c01381f9e?w=800',
    },
    {
      id: 'jiujitsu',
      slug: 'brazilian-jiu-jitsu',
      title: 'Jiu-Jitsu',
      description: 'Control, grappling, and submission fundamentals.',
      image: 'https://images.unsplash.com/photo-1747331796135-0e2354a712e4?w=800',
    },
  ];

  const trustPoints = [
    'Coach credentials listed publicly',
    'Public reviews and ratings',
    'You choose who trains you',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="DojoDash | Find a Professional Coach Near You"
        description="DojoDash is a mobile training network. Book private sessions in BJJ, self-defense, and personal training. The coach comes to you."
      />
      {/* Hero Section */}
      <section
        className="hero-section flex items-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
        }}
        data-testid="hero-section"
      >
        <div className="hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl stagger-children">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-6 h-6 text-[#C0A062]" />
              <span className="font-accent text-[#C0A062] tracking-widest">DOJODASH</span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight"
              data-testid="hero-headline"
            >
              Get coached <span className="text-[#C0A062]">anywhere.</span>
            </h1>

            <p className="text-lg sm:text-xl text-zinc-300 mb-8 max-w-2xl" data-testid="hero-subheadline">
              Find your coach. Pick your spot. Start training and transforming.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="btn-primary text-base px-8 py-6 animate-pulse-gold"
                data-testid="hero-cta-client"
              >
                Start Training
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/become-a-trainer')}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white text-base px-8 py-6"
                data-testid="hero-cta-trainer"
              >
                Become a Trainer
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="about" className="py-24 bg-[#09090B]" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="font-accent text-[#C0A062] text-sm tracking-widest mb-3 block">
              HOW IT WORKS
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Getting started is easier than your first workout.
            </h2>
          </div>

          <div className="relative max-w-5xl mx-auto" data-testid="how-it-works-steps">
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent"
            />
            <div className="grid md:grid-cols-3 gap-10 md:gap-6 relative">
              {[
                { step: '1', Icon: User, title: 'Create your profile', desc: 'Tell us your goals and what you want to learn.' },
                { step: '2', Icon: Search, title: 'Book a coach', desc: 'Browse professional coaches and book in minutes.' },
                { step: '3', Icon: Trophy, title: 'Train and progress', desc: 'Show up, train hard, and level up through the belt system.' },
              ].map(({ step, Icon, title, desc }) => (
                <div key={step} className="flex flex-col items-center text-center px-2" data-testid={`how-step-${step}`}>
                  <div className="relative z-10 w-14 h-14 rounded-full bg-[#09090B] border border-zinc-700 flex items-center justify-center mb-5">
                    <span className="font-accent text-2xl font-bold text-[#C0A062]">{step}</span>
                  </div>
                  <Icon className="w-6 h-6 text-zinc-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-1.5">{title}</h3>
                  <p className="text-sm text-zinc-400 max-w-xs">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature toggle: client view ↔ coach view */}
      <FeatureToggle />

      {/* Belt Progression, full width */}
      <section className="py-24 bg-gradient-to-b from-[#0F0F11] to-[#09090B]" data-testid="belt-progression-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-[#C0A062] text-sm tracking-widest mb-3 block">
              THE DOJODASH PHASE SYSTEM
            </span>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4 leading-tight" data-testid="belt-progression-headline">
              Progress You Can See. <span className="text-[#C0A062]">Rank You Can Earn.</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Most apps let you book a session. DojoDash lets you build a rank.
            </p>
          </div>

          {/* Belt circles row */}
          <div className="flex items-center justify-between gap-2 sm:gap-6 max-w-3xl mx-auto mb-6" data-testid="belt-row">
            {BELTS.map((b, i) => (
              <React.Fragment key={b.name}>
                <div className="flex flex-col items-center" data-testid={`belt-${b.name.toLowerCase()}`}>
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full border-4 border-zinc-900 ${b.cls} shadow-lg`} />
                  <p className="text-xs sm:text-sm text-zinc-300 mt-2 font-semibold">{b.name}</p>
                </div>
                {i < BELTS.length - 1 && (
                  <div className="flex-1 h-px bg-gradient-to-r from-zinc-700 to-zinc-700" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Discipline tracks tabs */}
          <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-6 sm:p-8 max-w-3xl mx-auto mt-12">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">All three tracks. Same 5-belt system.</p>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="discipline-tabs">
              {DISCIPLINE_TRACKS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTrack(t.id)}
                  data-testid={`discipline-tab-${t.id}`}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    activeTrack === t.id
                      ? 'bg-[#C0A062] text-black'
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 sm:gap-4" data-testid={`track-${activeTrack}`}>
              {BELTS.map((b) => (
                <div key={b.name} className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-zinc-900 ${b.cls}`} />
                  <p className="text-[10px] sm:text-xs text-zinc-400 mt-1.5">{b.name}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              {isAuthenticated && isClient ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
                  data-testid="belt-cta-dashboard"
                >
                  See your progress
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
                  data-testid="belt-cta-signup"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  See Your Progress
                </Button>
              )}
              {!isAuthenticated && (
                <p className="text-[11px] text-zinc-500 mt-3">Create an account to unlock your interactive belt tracker.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-[#0F0F11]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Choose Your Path</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Three categories, one progression system. Find your discipline.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((cat) => (
              <div key={cat.id} className="category-card rounded-lg overflow-hidden bg-[#18181B] border border-zinc-800" data-testid={`category-${cat.id}`}>
                <div
                  className="relative h-48 overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/disciplines/${cat.slug}`)}
                >
                  <img src={cat.image} alt={cat.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{cat.title}</h3>
                  <p className="text-zinc-400 text-sm mb-4">{cat.description}</p>
                  <Button
                    variant="outline"
                    className="w-full border-[#C0A062]/30 text-[#C0A062] hover:bg-[#C0A062]/10"
                    onClick={() => navigate(`/disciplines/${cat.slug}`)}
                    data-testid={`category-cta-${cat.id}`}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-24 bg-[#09090B]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8">
                <h3 className="text-lg font-bold text-white mb-6">Trust Stack</h3>
                <div className="space-y-4">
                  {trustPoints.map((point, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <span className="text-zinc-300">{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-6 leading-relaxed" data-testid="trust-disclaimer-fine">
                  All trainers self-report their credentials. DojoDash does not independently verify
                  certifications or conduct background checks.
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="font-accent text-[#C0A062] text-sm tracking-widest mb-4 block">
                CREDENTIALS, IN THE OPEN
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                You Choose Who <span className="text-[#C0A062]">Trains You.</span>
              </h2>
              <p className="text-zinc-400">
                Every coach on DojoDash lists their credentials, certifications, and experience publicly.
                You choose who trains you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F0F11]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Start Your Journey?</h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Join the first wave of clients and coaches building something real.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/register')} className="btn-primary text-base px-8 py-6" data-testid="cta-register">
              Create Your Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/trainers')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white text-base px-8 py-6" data-testid="cta-browse">
              Browse Coaches
            </Button>
          </div>
        </div>
      </section>

      {/* Waitlist removed (Jun 2026) */}

      {/* Footer */}
      <footer className="py-12 bg-[#09090B] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#C0A062]" />
              <span className="font-bold text-white">DOJODASH</span>
            </div>
            <p className="text-sm text-zinc-500">
              © 2026 DojoDash. Train Anywhere.
              <span className="mx-2">·</span>
              <Link to="/privacy" className="hover:text-zinc-300" data-testid="landing-footer-privacy-link">Privacy Policy</Link>
              <span className="mx-2">·</span>
              <Link to="/terms" className="hover:text-zinc-300" data-testid="landing-footer-terms-link">Terms of Service</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
